import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CILogo } from '../../components/CILogo';
import { 
  Users, Settings, LogOut, Plus, Search, CheckCircle, XCircle, Mail
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function MerchantAdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = sessionStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Unauthorized');

      const data = await response.json();
      if (!['super_admin', 'merchant_admin'].includes(data.role)) {
        toast.error('Admin access required');
        navigate('/admin/login');
        return;
      }

      setUser(data);
      loadTeam(token);
    } catch (error) {
      sessionStorage.removeItem('admin_token');
      navigate('/admin/login');
    }
  };

  const loadTeam = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/merchant/team`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTeam(data.team || []);
      }
    } catch (error) {
      console.error('Failed to load team:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const token = sessionStorage.getItem('admin_token');
    try {
      await fetch(`${API_URL}/api/admin/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) { console.error("Error:", e); }
    sessionStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    const token = sessionStorage.getItem('admin_token');
    const formData = new FormData(e.target);

    try {
      const response = await fetch(`${API_URL}/api/admin/merchant/team/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.get('email'),
          name: formData.get('name')
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail);
      }

      toast.success('Team member invited!', {
        description: `Temporary password: ${data.temp_password}`
      });
      setShowInviteModal(false);
      loadTeam(token);
    } catch (error) {
      toast.error(error.message || 'Failed to invite');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B1120]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#7A5CFF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#9CB0C7]">Loading...</p>
        </div>
      </div>
    );
  }

  const filteredTeam = team.filter(m => 
    m.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0B1120]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0A1628]/95 backdrop-blur-sm border-b border-[#1E3A5F]">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <CILogo size="sm" showText={false} />
            <div>
              <h1 className="text-xl font-bold text-white">Merchant Admin</h1>
              <p className="text-xs text-[#9CB0C7]">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="px-3 py-1 rounded-full bg-purple-500/10 text-purple-400 text-sm font-medium border border-purple-500/30">
              {user?.role?.replace('_', ' ')}
            </span>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 px-4 py-2 text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F] rounded-lg transition-colors"
            >
              <Settings className="w-4 h-4" />
              Dashboard
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-[#9CB0C7] hover:text-white hover:bg-[#1E3A5F] rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-2">Team Management</h2>
          <p className="text-[#9CB0C7]">Manage your team members and their access</p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mb-6">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CB0C7]" />
            <input
              type="text"
              placeholder="Search team..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg bg-[#111A2E] border border-[#22304A] text-white placeholder-[#9CB0C7] focus:outline-none focus:ring-2 focus:ring-[#7A5CFF]/50"
            />
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#7A5CFF] text-white rounded-lg hover:bg-[#7A5CFF]/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Invite Member
          </button>
        </div>

        {/* Team List */}
        <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 overflow-hidden">
          {filteredTeam.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-[#9CB0C7] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No team members yet</h3>
              <p className="text-[#9CB0C7] mb-4">Invite your first team member to get started</p>
              <button
                onClick={() => setShowInviteModal(true)}
                className="px-4 py-2 bg-[#7A5CFF] text-white rounded-lg hover:bg-[#7A5CFF]/90"
              >
                Invite Member
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-[#111A2E]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Member</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#22304A]">
                {filteredTeam.map((member) => (
                  <tr key={member.user_id} className="hover:bg-[#111A2E]/40">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3EF1C7] to-[#7A5CFF] flex items-center justify-center">
                          <span className="text-sm font-bold text-[#0A1628]">
                            {member.name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-white">{member.name}</p>
                          <p className="text-sm text-[#9CB0C7]">{member.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        member.role === 'merchant_admin' 
                          ? 'bg-purple-500/10 text-purple-400 border border-purple-500/30' 
                          : 'bg-gray-500/10 text-gray-400 border border-gray-500/30'
                      }`}>
                        {member.role?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {member.is_active ? (
                        <span className="flex items-center gap-1 text-[#8CF78E]">
                          <CheckCircle className="w-4 h-4" /> Active
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-[#F55454]">
                          <XCircle className="w-4 h-4" /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-[#9CB0C7] text-sm">
                      {member.created_at ? new Date(member.created_at).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Permissions Info */}
        <div className="mt-8 rounded-lg border border-[#22304A] bg-[#0E1426]/80 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Role Permissions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 rounded-lg bg-[#111A2E] border border-purple-500/30">
              <h4 className="font-medium text-purple-400 mb-2">Merchant Admin</h4>
              <ul className="text-sm text-[#9CB0C7] space-y-1">
                <li>• Full dashboard access</li>
                <li>• Create and manage deals</li>
                <li>• View analytics</li>
                <li>• Manage team members</li>
                <li>• Configure settings</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg bg-[#111A2E] border border-gray-500/30">
              <h4 className="font-medium text-gray-400 mb-2">Team Member (User)</h4>
              <ul className="text-sm text-[#9CB0C7] space-y-1">
                <li>• View dashboard</li>
                <li>• View deals and products</li>
                <li>• View analytics</li>
                <li>• No team management</li>
                <li>• No settings access</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#0E1426] rounded-lg border border-[#22304A] max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-white mb-6">Invite Team Member</h2>
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full px-4 py-2 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#7A5CFF]/50"
                  placeholder="team@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Name</label>
                <input
                  type="text"
                  name="name"
                  required
                  className="w-full px-4 py-2 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#7A5CFF]/50"
                  placeholder="John Doe"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-[#22304A] text-white hover:bg-[#1A2437]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 rounded-lg bg-[#7A5CFF] text-white hover:bg-[#7A5CFF]/90 flex items-center justify-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  Send Invite
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
